import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, fileUrl } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronLeft, Send, Users, UserPlus, Check, X, Loader2, Search, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import ChatComposer from '@/components/ChatComposer';
import UserLink from '@/components/UserLink';
import ImageLightbox from '@/components/ImageLightbox';

function Avatar({ user, size = 8 }) {
  const cls = `w-${size} h-${size} rounded-full bg-[#00F0FF] text-black grid place-items-center font-bold overflow-hidden`;
  if (user?.avatar_url) return <img src={fileUrl(user.avatar_url)} alt={user.name} className={`${cls} object-cover`} />;
  return <div className={cls} style={{ fontSize: size > 8 ? '14px' : '11px' }}>{(user?.name || '?')[0]?.toUpperCase()}</div>;
}

export default function Messages() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [tab, setTab] = useState('dms'); // dms | friends
  const [threads, setThreads] = useState([]);
  const [friends, setFriends] = useState({ accepted: [], pending_in: [], pending_out: [] });
  const [activeUser, setActiveUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  const loadAll = async () => {
    try {
      const [a, b] = await Promise.all([api.get('/dm'), api.get('/friends')]);
      setThreads(a.data); setFriends(b.data);
    } catch (err) { console.warn('[Messages] loadAll failed:', err); }
  };

  useEffect(() => {
    if (!user || user === false) return;
    loadAll();
    const i = setInterval(loadAll, 8000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const openChat = async (u) => {
    setActiveUser(u);
    try {
      const { data } = await api.get(`/dm/${u.user_id || u.id}`);
      setMessages(data);
    } catch { setMessages([]); }
  };

  useEffect(() => {
    if (!activeUser) return;
    const i = setInterval(async () => {
      try {
        const { data } = await api.get(`/dm/${activeUser.user_id || activeUser.id}`);
        setMessages(data);
      } catch (err) { console.warn('[Messages] DM poll failed:', err); }
    }, 5000);
    return () => clearInterval(i);
  }, [activeUser]);

  const send = async ({ text, image_path }) => {
    if (!activeUser) return;
    try {
      const { data } = await api.post(`/dm/${activeUser.user_id || activeUser.id}`, { text, image_path });
      setMessages((prev) => [...prev, data]);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  const doSearch = async (q) => {
    setSearch(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try { const { data } = await api.get('/users/search', { params: { q } }); setSearchResults(data); }
    catch { setSearchResults([]); }
  };

  const addFriend = async (uid) => {
    try { await api.post(`/friends/request/${uid}`); toast.success(t('messages.request_sent')); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || t('common.error')); }
  };
  const accept = async (fid) => { await api.post(`/friends/${fid}/accept`); loadAll(); };
  const remove = async (fid) => { await api.delete(`/friends/${fid}`); loadAll(); };

  if (!user || user === false) {
    return <div className="min-h-screen grid place-items-center text-white/60">{t('common.loading')}</div>;
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-4 md:px-6 bg-[#0A0A0A] text-white">
      <div className="max-w-6xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/50 hover:text-white">
          <ChevronLeft className="w-3 h-3" /> {t('nav.home')}
        </Link>
        <h1 className="font-display text-3xl lg:text-5xl font-black uppercase tracking-tighter mt-3">
          {t('messages.title')}
        </h1>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[320px,1fr] gap-4 h-[calc(100vh-220px)]">
          {/* LEFT - Tabs + lists */}
          <div className="glass rounded-2xl p-4 flex flex-col overflow-hidden">
            <div className="grid grid-cols-2 bg-white/5 rounded-lg p-1 border border-white/10">
              <button data-testid="msg-tab-dms" onClick={() => setTab('dms')} className={`text-xs uppercase tracking-wider font-bold py-2 rounded-md transition ${tab === 'dms' ? 'bg-[#FF007F] text-white' : 'text-white/60'}`}>
                <MessageCircle className="w-3.5 h-3.5 inline mr-1" /> {t('messages.tab.dms')}
              </button>
              <button data-testid="msg-tab-friends" onClick={() => setTab('friends')} className={`text-xs uppercase tracking-wider font-bold py-2 rounded-md transition ${tab === 'friends' ? 'bg-[#FF007F] text-white' : 'text-white/60'}`}>
                <Users className="w-3.5 h-3.5 inline mr-1" /> {t('messages.tab.friends')}
              </button>
            </div>

            <div className="mt-4 flex-1 overflow-y-auto no-scrollbar">
              {tab === 'dms' && (
                <>
                  {threads.length === 0 && <div className="text-white/40 text-sm text-center py-8">{t('messages.empty_threads')}</div>}
                  {threads.map((th) => (
                    <button key={th.user_id} data-testid={`msg-thread-${th.user_id}`} onClick={() => openChat(th)} className={`w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/5 transition ${activeUser?.user_id === th.user_id ? 'bg-white/10' : ''}`}>
                      <Avatar user={th} size={10} />
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-sm truncate">{th.name}</span>
                          {th.unread > 0 && <span className="text-[10px] bg-[#FF007F] rounded-full px-1.5 py-0.5 font-bold">{th.unread}</span>}
                        </div>
                        <div className="text-xs text-white/50 truncate">
                          {th.last_from_me && '↗ '}{th.last_text}
                        </div>
                      </div>
                    </button>
                  ))}
                </>
              )}

              {tab === 'friends' && (
                <div className="space-y-4">
                  <div>
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 absolute left-3 top-3 text-white/40" />
                      <Input
                        data-testid="msg-friends-search"
                        value={search}
                        onChange={(e) => doSearch(e.target.value)}
                        placeholder={t('messages.search_user')}
                        className="bg-black/40 border-white/10 text-white placeholder:text-white/30 pl-9 h-9 text-sm"
                      />
                    </div>
                    {searchResults.map((u) => (
                      <div key={u.id} className="flex items-center gap-2 p-2 mt-1 rounded-lg bg-white/5">
                        <Avatar user={u} size={8} />
                        <span className="flex-1 text-sm truncate">{u.name}</span>
                        <Button data-testid={`msg-add-friend-${u.id}`} onClick={() => addFriend(u.id)} size="sm" variant="ghost" className="h-7 text-[#00F0FF] hover:bg-[#00F0FF]/10">
                          <UserPlus className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  {friends.pending_in.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-2">{t('messages.pending_in')}</div>
                      {friends.pending_in.map((f) => (
                        <div key={f.id} className="flex items-center gap-2 p-2 rounded-lg bg-white/5 mb-1">
                          <Avatar user={f} size={8} />
                          <span className="flex-1 text-sm truncate">{f.name}</span>
                          <button data-testid={`msg-accept-${f.id}`} onClick={() => accept(f.id)} className="text-emerald-400 hover:bg-emerald-500/10 rounded p-1"><Check className="w-3.5 h-3.5" /></button>
                          <button data-testid={`msg-decline-${f.id}`} onClick={() => remove(f.id)} className="text-red-400 hover:bg-red-500/10 rounded p-1"><X className="w-3.5 h-3.5" /></button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-2">{t('messages.friends_count')} ({friends.accepted.length})</div>
                    {friends.accepted.length === 0 && <div className="text-white/40 text-xs text-center py-4">{t('messages.no_friends')}</div>}
                    {friends.accepted.map((f) => (
                      <button key={f.id} data-testid={`msg-friend-${f.id}`} onClick={() => openChat(f)} className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 transition">
                        <Avatar user={f} size={8} />
                        <span className="flex-1 text-sm truncate text-left">{f.name}</span>
                        <Send className="w-3.5 h-3.5 text-white/40" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT - Chat */}
          <div className="glass rounded-2xl flex flex-col overflow-hidden">
            {!activeUser ? (
              <div className="flex-1 grid place-items-center text-white/40 text-sm p-8 text-center">
                <div>
                  <MessageCircle className="w-10 h-10 mx-auto text-white/20" />
                  <div className="mt-3">{t('messages.select_conv')}</div>
                </div>
              </div>
            ) : (
              <>
                <div className="border-b border-white/10 p-4 flex items-center gap-3">
                  <Avatar user={activeUser} size={10} />
                  <div className="font-bold"><UserLink userId={activeUser.user_id || activeUser.id} name={activeUser.name} /></div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="msg-conversation">
                  {messages.length === 0 && <div className="text-white/40 text-sm text-center py-8">{t('messages.empty_conv')}</div>}
                  {messages.map((m) => {
                    const mine = m.from_id === user.id;
                    return (
                      <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${mine ? 'bg-[#FF007F] text-white' : 'bg-white/10 text-white'}`}>
                          {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                          {m.image_path && (
                            <button type="button" onClick={() => setLightbox(m.image_path)} className="block mt-1" data-testid={`msg-image-${m.id}`}>
                              <img src={fileUrl(m.image_path)} alt="" className="max-h-56 rounded-lg hover:opacity-90 transition" />
                            </button>
                          )}
                          <div className="text-[10px] opacity-60 mt-1">{new Date(m.created_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="border-t border-white/10 p-3">
                  <ChatComposer
                    onSubmit={send}
                    placeholder={t('messages.placeholder')}
                    rows={1}
                    sendVariant="cyan"
                    testIdPrefix="msg"
                    sendOnEnter
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <ImageLightbox src={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
